export type Product = 'Mambu MCP' | 'Mambu Agents'

// Tab order everywhere (MCP left of Agents — prototype `applyScenarioTabs`).
// Readonly so no caller can reorder or extend the one shared list.
export const PRODUCTS: readonly Product[] = ['Mambu MCP', 'Mambu Agents'] as const

// 'Expired' is a third stored status, not the absence of the other two — a
// contract that ran out is not the same as one that was never signed, which
// is still 'Not Contracted' (the synthesised value for a product with no
// subscription row at all). See models/subscription.py.
export type ContractStatus = 'Trial' | 'Contracted' | 'Expired' | 'Not Contracted'

/** The caller's portal role (MS-997; ARCHITECTURE_DECISIONS.md §57): an
 * Admin also sees Settings › Access, a Controller every other page. Read from
 * the grant on every request, never from a token claim. */
export type PortalRole = 'admin' | 'controller'

export const PORTAL_ROLES: readonly PortalRole[] = ['admin', 'controller'] as const

export interface SessionResponse {
  displayName: string | null
  email: string | null
  // string, not number: 64-bit TSIDs exceed JS's safe-integer range — the
  // backend serializes every id field as a string for exactly this reason
  // (see billing_portal/api/schemas/notifications.py's NotificationResponse.id).
  accountId: string | null
  accountName: string | null
  // The grant's role (MS-997). Null only from an older cached profile.
  role: PortalRole | null
}

/** GET /api/v1/auth/config (MS-977, MS-997): where to send the browser
 * after POST /logout so the Cognito session ends too. Cognito is the only
 * sign-in, so there is no provider to tell apart any more. */
export interface AuthConfig {
  logoutUrl: string | null
}

/** One row of Settings › Access (GET /api/v1/settings/access, MS-997 B3).
 * `id` is the grant's TSID as a string (the same 64-bit reason as
 * SessionResponse.accountId); `grantedAt` an ISO timestamp, null only for a
 * grant that predates the column. */
export interface PortalAccessGrant {
  id: string
  email: string
  role: PortalRole
  grantedAt: string | null
}

/** One row of GET /api/v1/auth/accounts: an active account the signed-in
 * email holds a grant on (MS-997 B7). `id` is a string for the same 64-bit TSID
 * reason as SessionResponse.accountId. */
export interface AuthAccount {
  id: string
  code: string
  name: string
}

export interface SubscriptionSummary {
  product: Product
  contractStatus: ContractStatus
  // 'analyse' for Mambu Agents; null for Mambu MCP, which has no tiers, and
  // null while the product isn't contracted.
  tier: string | null
  trialEndDate: string | null
  // Per product, not per account: the two products' contract dates may differ,
  // so each subscription carries its own pair rather than the response having
  // one account-wide pair at the top level. Null until the product is
  // contracted; the end date stays null on an open-ended contract. Nullable
  // rather than optional — the backend always emits the keys, and a required
  // key keeps the compiler pointing at every call site if the shape moves
  // again, which `?:` would silently absorb.
  contractStartDate: string | null
  contractEndDate: string | null
}

export interface ContractResponse {
  currency: string
  subscriptions: SubscriptionSummary[]
}

/** One admin-bought On Demand purchase still delivering volume (MS-820,
 * read from `purchases`). Mambu MCP: `orderedQuantity` arrives every calendar
 * month from `startDate` to `endDate` (its 12-month renewal window, capped at
 * the contract end). Mambu Agents: the credits added once, valid until
 * `endDate` (the contract end) or open-ended when null. */
export interface Bundle {
  id: string
  orderedQuantity: number
  consumedQuantity: number
  remaining: number
  startDate: string
  endDate: string | null
  /** Mambu MCP only: arrives again every calendar month until `endDate` (Q2).
   * False for a backfilled legacy order — one pot, counted once, today. */
  renewsMonthly: boolean
}

/** §5.4a's composition of a balance (MS-820 Q11): raw remaining per channel
 * of the purchases valid today, the open holds account-wide, and the size of
 * that volume. `orderForm + onDemand − held` (≥ 0) is the balance. */
export interface BalanceByChannel {
  orderForm: number
  onDemand: number
  held: number
  capacity: number
  /** The part of `orderForm` still valid next calendar month — the committed
   * pot's remainder (and an Agents annual credit's) — carried forward by the
   * forecasts instead of re-adding `committedVolume` every period. */
  orderFormCarriedOver: number
}

interface ProductQuotaFields {
  product: Product
  includedVolume: number
  defaultUserLimit: number | null
  overageEnabled: boolean
  currentPeriodStart: string | null
  currentPeriodEnd: string | null
  currentPeriodCount: number
  priceExcess: string
  priceOnDemand: string
  priceOrderForm: string
  bundles: Bundle[]
  // MS-820 — see api/schemas/quota.py for each field's source.
  trialAllowance: number
  trialConsumed: number
  committedVolume: number
  /** included + committed (Q18); null when nothing is contracted. */
  contractedVolume: number | null
  /** get_balance on Contracted, the trial allowance left on Trial, null otherwise. */
  balanceRemaining: number | null
  balanceByChannel: BalanceByChannel | null
  /** MCP: On Demand volume arriving this month. */
  onDemandMonthly: number
  /** Trailing average of the last ≤12 completed months (Q15), or null. */
  avgMonthlyConsumption: number | null
  /** The On Demand bundle (Q3); both null when not configured. */
  bundleUnit: number | null
  bundlePrice: string | null
  // MS-822 — `defaultUserLimit` is the default in force (null while off).
  /** The default limit is switched on. */
  defaultUserLimitEnabled: boolean
  /** The default's figure whether or not it is on — what switching it on
   * restores (OQ2); null when none was ever set. */
  storedDefaultUserLimit: number | null
  /** MS-996 — a user with no limit row is registered on their first usage
   * (true) or refused (false: only the users listed are served). Optional
   * only for an API from before MS-996, which sends none (always automatic). */
  autoRegisterUsers?: boolean
}

/** One product's `/api/v1/quota` row. A Trial row should always carry its
 * `trialEndDate` — the database refuses one without it (MS-819 Q6,
 * `ck_subscriptions_trial_has_end_date`) — but a row that slips past the
 * constraint is still sent, dateless (and logged), rather than failing the
 * whole read. So the type keeps it nullable on every status, and the trial
 * banner is left out when it is missing. */
export type ProductQuota = ProductQuotaFields & {
  contractStatus: ContractStatus
  trialEndDate: string | null
}

export interface Tenant {
  id: string
  tenantName: string
  tenantUrl: string
}

/** One bar on the Usage page's By-tenant or By-user card.
 *
 * Both products draw one series of `total` (MS-996, spec §4.2-4); there is no
 * per-invocation-type split.
 *
 * `label` is null when the subject no longer resolves (a detached tenant, or
 * usage settled with no tenant at all); the page supplies a translated
 * fallback rather than the API inventing an English one. */
export interface UsageBreakdownRow {
  id: string
  label: string | null
  total: number
}

/** `GET /api/v1/usage/summary` — the whole Usage page for one product and one
 * calendar month, read from the `usage_daily` rollup.
 *
 * `totalAvailableVolume`/`percentageUsed` are nullable and currently always
 * null: reconstructing the balance at the start of a past month needs the
 * per-month allowance history MS-877 introduces. The page renders an
 * em-dash. They are not `contractedVolume + onDemandVolume` — that sum omits
 * carried-over balance and reads low, so it is wrong rather than approximate. */
export interface UsageSummary {
  product: Product
  unit: 'invocations' | 'credits'
  month: string
  periodStart: string
  periodEnd: string
  availableMonths: string[]
  contractedVolume: number
  onDemandVolume: number
  totalAvailableVolume: number | null
  consumedVolume: number
  percentageUsed: number | null
  byTenant: UsageBreakdownRow[]
  byUser: UsageBreakdownRow[]
}

export interface EndUser {
  // The *registration* id — one row per person per account. Scoped per product
  // by the `?product=` filter every call site passes, so it is still unique
  // within a page; it is not unique across products.
  id: string
  product: Product
  // Nullable since the registration was re-keyed onto email: a person added by
  // an admin has no OAuth subject until they first sign in. Display-only.
  subjectId: string | null
  // NOT NULL server-side: the email is the registration key and, per §16h, the
  // only identifier the billing engine holds for a person.
  email: string
  fullName: string | null
  tenantId: string | null
  tenantName: string | null
  /** The user's own limit; null means they follow the account default. */
  userLimit: number | null
  /** The limit in force today (a lowered limit's predecessor until the 1st). */
  effectiveLimit: number | null
  usageCount: number
  active: boolean
  /** On an enforced default with no limit of their own (MS-822). */
  isDefault: boolean
}

export interface EndUserPage {
  items: EndUser[]
  total: number
  page: number
  pageSize: number
}

/** One Order-history row (§24h; MS-820): an On Demand purchase. `bundles` ×
 * `unitPrice` (price of ONE bundle) = `cost`; `bundleUnit` is one bundle's
 * content, which is what the row describes (§24h-desc). `reference` is
 * AAAA-YYYY-NNNN, null on a legacy pre-MS-820 purchase. */
export interface Order {
  id: string
  product: Product
  reference: string | null
  purchaseDate: string
  purchasedAt: string | null
  description: string | null
  channel: 'On Demand'
  bundles: number
  bundleUnit: number
  unitPrice: string
  cost: string
  quantity: number
  status: string
  byEmail: string | null
  byName: string | null
  estimatedInvoiceDate: string | null
  /** Mambu MCP bought since MS-820: the bundle renews every month ("/ mo").
   * False for Mambu Agents and for a backfilled legacy order. */
  renewsMonthly: boolean
}

/** One row of the Excess Volume month table (MS-821): `month` is `YYYY-MM`,
 * `excessCost` the row's ceil(units / 1000) × stored rate, as a decimal string. */
export interface ExcessMonth {
  month: string
  excessVolume: number
  excessCost: string
}

/** `GET /orders/overage-summary` — the Excess Volume card. The totals are the
 * sums of `months` (OQ-13); the period is the read-time 3-month billing
 * period (OQ-2); `ratePer1000` is today's excess price per 1,000, for the
 * confirm's "Charged at" row only. A Trial row has nulls and no months. */
export interface OverageSummary {
  product: Product
  excessThisPeriod: number
  excessCost: string
  periodStart: string | null
  periodEnd: string | null
  ratePer1000: string | null
  months: ExcessMonth[]
}

/** One notification as the signed-in Controller sees it (MS-868,
 * ARCHITECTURE_DECISIONS.md §49). `read`/`dismissedAt` are this caller's own
 * state. `current`/`limit` are the row's own crossing figures (PO Q16), as
 * fixed-point decimal text; they are null on a `trial_ending` row and on a row
 * the previous release wrote during a rollback window. `threshold` is null on
 * a `trial_ending` row. `onTrial` is the subscription's status when it fired. */
export interface ThresholdNotification {
  id: string
  kind: 'threshold' | 'trial_ending'
  product: Product
  subjectType: 'account' | 'end_user'
  subjectId: string | null
  cycleStart: string
  cycleEnd: string | null
  threshold: 80 | 90 | 100 | null
  current: string | null
  limit: string | null
  onTrial: boolean
  /** Whether excess volume was on when the row fired; null when not known
   * (a row from before this was recorded), read as off. */
  excessOn: boolean | null
  notifiedAt: string
  read: boolean
  dismissedAt: string | null
}
